import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { Adapter } from "src/app/core/adapter";
import { Model } from "src/app/core/models/model";
import { LatLon } from "src/app/shared/components/bubble-map/bubble-map.component";
import { Alert } from "./alert.model";
import { Meter } from "./meter.model";
import { ShowerHead } from "./shower-head.model";

export enum GatewayType {
  LinkioGateway = "LinkioGateway",
  LoRaGateway = "LoRaGateway",
}

export interface GatewayData {
  showerheadUuids: string[];
  meterUuids: string[];
  alertIds: number[];
  activeAlertIds: number[];
  finishedAlertIds: number[];
  location: LatLon;
}

export type GatewaysData = { [id: number]: GatewayData };

export class Gateway implements Model {
  public meters: Meter[] = [];
  public showerheads: ShowerHead[] = [];
  public alerts: Alert[] = [];

  constructor(
    public gateway_uuid: string,
    public first_seen: DateTime,
    public last_seen: DateTime,
    public type: GatewayType,
    public device: string,
    public label: string // for user label (not implemented yet)
  ) {}

  public get universalLabel(): string {
    if (typeof this.label === "string" && this.label.trim().length > 0)
      return this.label.trim();
    else if (this.device === "LinkioGateway") return "Hydrao Connect";
    else return this.device;
  }

  public get fixedLastSeen(): DateTime {
    if (!this.last_seen || this.last_seen === null) return this.first_seen;
    return this.last_seen;
  }

  public get lastSeenDuration(): number {
    if (!this.fixedLastSeen || this.fixedLastSeen === null) return 0;

    let seconds =
      (DateTime.local().toMillis() - this.fixedLastSeen.toMillis()) / 1000 / 60;
    // return seconds > 60 ? Math.round(seconds) : seconds;
    return seconds;
  }

  public get lastSeenTooltip() {
    let hours = Math.round(this.lastSeenDuration / 60);
    let minutes = ("0" + (this.lastSeenDuration - 60 * hours)).slice(-2);
    if (this.lastSeenDuration == 0) return "";
    else if (this.lastSeenDuration < 3) {
      return $localize`Seen in the last 3 minutes`;
    } else if (this.lastSeenDuration <= 1440) {
      return $localize`last seen ${hours}:hh: hours ${minutes}:minutes: minutes ago`;
    } else {
      try {
        return $localize`Last seen on ${new Intl.DateTimeFormat(
          $localize.locale || "en",
          {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false, // Pour avoir un format 24h
            timeZone: Settings.defaultZone.name,
          }
        ).format(this.fixedLastSeen.toJSDate())}:date:`;
      } catch (err) {
        console.error("FAILED to format last seen", this.last_seen);
        return "";
      }
    }
  }

  public lastSeenRelative(locale: string = $localize.locale) {
    return this.fixedLastSeen
      ? this.fixedLastSeen.setLocale(locale).toRelative()
      : null;
  }

  public get firstSeenFormatter() {
    let value = "";
    if (this.first_seen) {
      value = new Intl.DateTimeFormat($localize.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // pour un format 24h
        timeZone: Settings.defaultZone.name,
      }).format(this.first_seen.toJSDate());
    }
    return value;
  }

  public get lastSeenFormatter() {
    let value = "";
    if (this.fixedLastSeen) {
      value = new Intl.DateTimeFormat($localize.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // pour un format 24h
        timeZone: Settings.defaultZone.name,
      }).format(this.fixedLastSeen.toJSDate());
    }
    return value;
  }

  public clone(): Gateway {
    const clone = new Gateway(
      this.gateway_uuid,
      this.first_seen,
      this.last_seen,
      this.type,
      this.device,
      this.label
    );

    return clone;
  }
}

@Injectable({
  providedIn: "root",
})
export class GatewayAdapter implements Adapter<Gateway> {
  constructor() {}

  adapt(item: any): Gateway {
    return new Gateway(
      item.gateway_uuid,
      item.first_seen
        ? DateTime.fromISO(item.first_seen, { zone: Settings.defaultZone })
        : null,
      item.last_seen
        ? DateTime.fromISO(item.last_seen, { zone: Settings.defaultZone })
        : null,
      item.type,
      item.device,
      item.label
    );
  }
}
